class Header {
  private headerElement: HTMLElement | null;

  // Dropdown
  private dropdownContainer?: HTMLElement | null;
  private dropdownButton?: HTMLButtonElement | null;
  private dropdownMenu?: HTMLElement | null;

  // Mobile Nav
  private mobileButton?: HTMLButtonElement | null;
  private mobileMenu?: HTMLElement | null;
  private overlayElement?: HTMLElement | null;
  private isMobileNavEnabled: boolean = false;
  private _boundToggleMobileNav: () => void;
  private _boundCloseMobileNav: () => void;
  private _updateHeight: () => void;

  // Sticky
  private scrollThreshold?: number;
  private isScrollTicking: boolean = false;
  private isResizeTicking: boolean = false;
  private isStickyEnabled: boolean = false;
  private readonly stickyBreakpoint: number = 1025;
  private _boundOnScroll: () => void;

  constructor() {
    // Bind des méthodes
    this._boundOnScroll = this._onScroll.bind(this);
    this._boundToggleMobileNav = this._toggleMobileNav.bind(this);
    this._boundCloseMobileNav = this._closeMobileNav.bind(this);
    this._updateHeight = this._updateMobileNavHeight.bind(this);

    this.headerElement = document.querySelector('header');
    if (!this.headerElement) return;

    // Sélection des éléments
    this.mobileButton = this.headerElement.querySelector('[data-header-button]');
    this.mobileMenu = this.headerElement.querySelector('[data-header-menu]');
    this.overlayElement = this.headerElement.querySelector('[data-header-overlay]');

    this._initDropdown();

    // Gestion initiale
    this._handleResize();
    window.addEventListener('resize', () => this._onResize());
  }

  // ===================================================================
  // Dropdown 
  // ===================================================================

  private _initDropdown(): void {
    this.dropdownContainer = this.headerElement?.querySelector('[data-header-dropdown]');
    if (!this.dropdownContainer) return;

    this.dropdownButton = this.dropdownContainer.querySelector('button[aria-haspopup="true"]');
    const menuId = this.dropdownButton?.getAttribute('aria-controls');

    if (!this.dropdownButton || !menuId) return;
    this.dropdownMenu = document.getElementById(menuId);
    if (!this.dropdownMenu) return;

    this.dropdownButton.addEventListener('click', () => this.toggleDropdown());

    this.dropdownContainer.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeDropdown();
      }
    });
  }

  private toggleDropdown(): void {
    const isExpanded = this.dropdownButton?.getAttribute('aria-expanded') === 'true';
    isExpanded ? this.closeDropdown() : this.openDropdown();
  }

  private openDropdown(): void {
    this.dropdownMenu?.classList.remove('hidden');
    this.dropdownButton?.setAttribute('aria-expanded', 'true');
  }

  private closeDropdown(): void {
    this.dropdownMenu?.classList.add('hidden');
    this.dropdownButton?.setAttribute('aria-expanded', 'false');
  }

  // ===================================================================
  // Gestionnaires de Resize / Scroll
  // ===================================================================

  private _onResize(): void {
    if (!this.isResizeTicking) {
      window.requestAnimationFrame(() => {
        this._handleResize();
        this.isResizeTicking = false;
      });
      this.isResizeTicking = true;
    }
  }

  private _handleResize(): void {
    const isDesktop = window.innerWidth > this.stickyBreakpoint;

    // Logique Sticky (Desktop)
    if (isDesktop && !this.isStickyEnabled) {
      this._initStickyHeader();
      this.isStickyEnabled = true;
    } else if (!isDesktop && this.isStickyEnabled) {
      this._destroyStickyHeader();
      this.isStickyEnabled = false;
    }

    // Logique Mobile Nav (Mobile)
    if (!isDesktop && !this.isMobileNavEnabled) {
      this._initMobileNav();
      this._updateMobileNavHeight();
      this.isMobileNavEnabled = true;
      this.mobileMenu?.setAttribute('aria-hidden', 'true')
    } else if (isDesktop && this.isMobileNavEnabled) {
      this._destroyMobileNav();
      this.isMobileNavEnabled = false;
      this.mobileMenu?.removeAttribute('aria-hidden');
    }
  }

  // ===================================================================
  // Sticky  
  // ===================================================================

  private _initStickyHeader(): void {
    this.scrollThreshold = window.innerHeight * 0.8;
    this._updateStickyState();
    window.addEventListener('scroll', this._boundOnScroll, { passive: true });
  }

  private _destroyStickyHeader(): void {
    window.removeEventListener('scroll', this._boundOnScroll);
    this.headerElement?.classList.remove('sticky');
  }

  private _onScroll(): void {
    if (!this.isScrollTicking) {
      window.requestAnimationFrame(() => {
        this._updateStickyState();
        this.isScrollTicking = false;
      });
      this.isScrollTicking = true;
    }
  }

  private _updateStickyState(): void {
    if (this.scrollThreshold) {
      if (window.scrollY > this.scrollThreshold) {
        this.headerElement?.classList.add('sticky');
      } else if (window.scrollY == 0) {
        this.headerElement?.classList.remove('sticky');
      }
    }
  }

  // ===================================================================
  // Mobile Nav  
  // ===================================================================

  /**
   * Attache les listeners au bouton mobile et à l'overlay.
   */
  private _initMobileNav(): void {
    if (!this.mobileButton || !this.mobileMenu) return;

    this.mobileButton.addEventListener('click', this._boundToggleMobileNav);
    this.overlayElement?.addEventListener('click', this._boundCloseMobileNav);
  }

  /**
   * Supprime les listeners et nettoie les classes 'active'.
   */
  private _destroyMobileNav(): void {
    if (!this.mobileButton) return;

    this.mobileButton.removeEventListener('click', this._boundToggleMobileNav);
    this.overlayElement?.removeEventListener('click', this._boundCloseMobileNav);

    // Nettoyage des classes
    this._closeMobileNav();
  }

  /**
   * (Appelé par le bouton) Ajoute ou supprime la classe 'active' sur tous les éléments.
   */
  private _toggleMobileNav(): void {
    const isCurrentlyExpanded = this.mobileButton?.getAttribute('aria-expanded') === 'true';
    const newExpandedState = !isCurrentlyExpanded;

    const closeLabel = 'Fermer le menu';
    const openLabel = 'Ouvrir le menu';
    this.mobileButton?.setAttribute('aria-label', newExpandedState ? closeLabel : openLabel);

    this.mobileButton?.setAttribute('aria-expanded', String(newExpandedState));
    if (newExpandedState) {
      this.mobileMenu?.removeAttribute('aria-hidden');
    } else {
      this.mobileMenu?.setAttribute('aria-hidden', 'true');
    }

    this.mobileButton?.classList.toggle('active', newExpandedState);
    this.mobileMenu?.classList.toggle('active', newExpandedState);
    this.overlayElement?.classList.toggle('active', newExpandedState);
    document.querySelector('body')?.classList.toggle('unscrollable', newExpandedState);
  }

  /**
   * (Appelé par l'overlay ou destroy) Force la fermeture du menu.
   */
  private _closeMobileNav(): void {
    this.mobileButton?.setAttribute('aria-expanded', 'false');
    this.mobileMenu?.setAttribute('aria-hidden', 'true')
    this.mobileButton?.classList.remove('active');
    this.mobileMenu?.classList.remove('active');
    this.overlayElement?.classList.remove('active');
    document.querySelector('body')?.classList.remove('unscrollable');
  }

  private _updateMobileNavHeight(): void {
    if (!this.mobileMenu || !this.headerElement) return;
    const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    const headerHeight = this.headerElement.getBoundingClientRect().height;
    const maxHeight = viewportHeight - headerHeight;
    this.mobileMenu.style.maxHeight = `${maxHeight}px`;
  }
}

export const header = new Header();